import type { DashboardRole, Session } from '@/shared/types/session';
import { STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { localStorageUtil } from '@/shared/utils/local-storage';
import { clearAuthToken } from '@/shared/utils/auth-token';
import { clearStoredPlan } from '@/shared/utils/stored-plan';
import { clearStoredAccess } from '@/shared/utils/stored-access';

/**
 * Browser-side helpers for the dashboard session.
 *
 * Storage strategy — localStorage primary, cookie secondary:
 *   - localStorage holds the parsed `Session` so reads are a single
 *     `JSON.parse` (no cookie-string parsing on every hook tick).
 *   - The `ps_dev_session` cookie is kept in lock-step so the axios
 *     interceptor's same-origin path still works and so older tabs that
 *     read the cookie don't silently lose state.
 *   - Reads prefer localStorage; cookie acts as a fallback if a tab was
 *     opened from a link before localStorage was populated.
 *
 * Single source of truth: this file decides what's in either store.
 * Components read the session via the `useSession` hook (which calls
 * `readSessionCookie` via TanStack Query), never via raw document.cookie
 * or window.localStorage reads.
 */

const SESSION_COOKIE = 'ps_dev_session';
const SESSION_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

interface SessionCookiePayload {
    userId?:     string;
    email?:      string;
    fullName?:   string;
    tenantId?:   string;
    tenantSlug?: string;
    tenantName?: string;
    role?:       DashboardRole;
    isDevMode?:  boolean;
}

export function readSessionCookie(): Session | null {
    const fromLocal = localStorageUtil.get<Session>(STORAGE_KEYS.SESSION);
    if (fromLocal && fromLocal.tenantSlug && fromLocal.userId) return fromLocal;

    if (typeof document === 'undefined') return null;
    const raw = parseCookie(document.cookie)[SESSION_COOKIE];
    if (!raw) return null;
    try {
        const decoded = decodeURIComponent(raw);
        const data = JSON.parse(decoded) as SessionCookiePayload;
        if (!data.tenantSlug || !data.userId) return null;
        const session: Session = {
            userId:     data.userId,
            email:      data.email ?? '',
            fullName:   data.fullName ?? '',
            tenantId:   data.tenantId ?? '',
            tenantSlug: data.tenantSlug,
            tenantName: data.tenantName ?? data.tenantSlug,
            role:       data.role ?? 'tenant_admin',
            isDevMode:  data.isDevMode ?? false,
        };
        // Backfill localStorage so subsequent reads short-circuit above.
        localStorageUtil.set(STORAGE_KEYS.SESSION, session);
        return session;
    } catch {
        return null;
    }
}

export function writeSessionCookie(payload: SessionCookiePayload): void {
    const value = encodeURIComponent(JSON.stringify(payload));
    document.cookie = `${SESSION_COOKIE}=${value}; path=/; max-age=${SESSION_MAX_AGE_SECONDS}; samesite=lax`;

    if (payload.tenantSlug && payload.userId) {
        const session: Session = {
            userId:     payload.userId,
            email:      payload.email ?? '',
            fullName:   payload.fullName ?? '',
            tenantId:   payload.tenantId ?? '',
            tenantSlug: payload.tenantSlug,
            tenantName: payload.tenantName ?? payload.tenantSlug,
            role:       payload.role ?? 'tenant_admin',
            isDevMode:  payload.isDevMode ?? false,
        };
        localStorageUtil.set(STORAGE_KEYS.SESSION, session);
        // Bearer token + plan are written explicitly by the caller —
        // useDevLogin stores the server-issued JWT + plan; useContinueAsDemo
        // mints a random opaque token and clears the plan.
    }
}

export function clearSessionCookie(): void {
    document.cookie = `${SESSION_COOKIE}=; path=/; max-age=0; samesite=lax`;
    localStorageUtil.remove(STORAGE_KEYS.SESSION);
    clearAuthToken();
    clearStoredPlan();
    clearStoredAccess();
}

function parseCookie(cookieHeader: string): Record<string, string> {
    const out: Record<string, string> = {};
    for (const part of cookieHeader.split(';')) {
        const eq = part.indexOf('=');
        if (eq === -1) continue;
        const k = part.slice(0, eq).trim();
        const v = part.slice(eq + 1).trim();
        if (k) out[k] = v;
    }
    return out;
}
